/* Daily Dons — motion.js
   Replaces the original site's GSAP timelines with IntersectionObserver +
   CSS transitions. Durations, delays, staggers and easing curves are carried
   in CSS custom properties on the elements (see base.css + section CSS), so
   this module only decides WHEN a section reveals — never how it looks.

   Progressive enhancement: without JS the reveal classes never hide content
   (initial hidden state is scoped to html.js in CSS). */
(function () {
  'use strict';

  var reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)');

  /* ---- Scroll reveals -------------------------------------------------
     [data-reveal-group] wrappers get .is-revealed when their top crosses
     85% of the viewport (the original's ScrollTrigger `start: "top 85%"`).
     Standalone [data-reveal] elements observe themselves. */
  function initReveals(root) {
    root = root || document;
    var groups = root.querySelectorAll('[data-reveal-group]:not(.is-revealed)');
    var singles = root.querySelectorAll('[data-reveal][data-reveal-self]:not(.is-revealed)');

    if (reduceMotion.matches || !('IntersectionObserver' in window)) {
      groups.forEach(function (el) { el.classList.add('is-revealed'); });
      singles.forEach(function (el) { el.classList.add('is-revealed'); });
      return;
    }

    var io = new IntersectionObserver(function (entries) {
      entries.forEach(function (entry) {
        if (entry.isIntersecting || entry.boundingClientRect.top < 0) {
          entry.target.classList.add('is-revealed');
          io.unobserve(entry.target);
        }
      });
    }, { rootMargin: '0px 0px -15% 0px', threshold: 0 });

    groups.forEach(function (el) { io.observe(el); });
    singles.forEach(function (el) { io.observe(el); });
  }

  /* ---- Immediate (page-load) reveals ---------------------------------
     [data-reveal-load] wrappers replay the original entrance timelines
     (hero, article heroes, product hero) right after first paint. */
  function initLoadReveals(root) {
    root = root || document;
    var els = root.querySelectorAll('[data-reveal-load]:not(.is-revealed)');
    if (!els.length) return;
    requestAnimationFrame(function () {
      requestAnimationFrame(function () {
        els.forEach(function (el) { el.classList.add('is-revealed'); });
      });
    });
  }

  /* ---- Count-up (Savings section $0 → $88, 1.2s power2.out) ---------- */
  function initCountUps(root) {
    root = root || document;
    var els = root.querySelectorAll('[data-count-up]:not([data-counted])');
    if (!els.length) return;

    function animate(el) {
      el.setAttribute('data-counted', 'true');
      var target = parseFloat(el.getAttribute('data-count-up')) || 0;
      var prefix = el.getAttribute('data-count-prefix') || '';
      if (reduceMotion.matches) { el.textContent = prefix + Math.round(target); return; }
      var dur = parseFloat(el.getAttribute('data-count-duration')) || 1200;
      var start = null;
      function frame(ts) {
        if (start === null) start = ts;
        var t = Math.min(1, (ts - start) / dur);
        var eased = 1 - Math.pow(1 - t, 3); /* power2.out */
        el.textContent = prefix + Math.round(target * eased);
        if (t < 1) requestAnimationFrame(frame);
      }
      requestAnimationFrame(frame);
    }

    if (!('IntersectionObserver' in window)) { els.forEach(animate); return; }
    var io = new IntersectionObserver(function (entries) {
      entries.forEach(function (entry) {
        if (entry.isIntersecting) { animate(entry.target); io.unobserve(entry.target); }
      });
    }, { rootMargin: '0px 0px -15% 0px' });
    els.forEach(function (el) { io.observe(el); });
  }

  /* ---- Hero parallax (desktop only, scrub-smoothed) -------------------
     Mirrors gsap.to(img, { yPercent: 8, scrub: 0.5 }) over the hero's
     scroll range, with a small lerp for the 0.5s scrub smoothing. */
  function initParallax() {
    var wrap = document.querySelector('[data-parallax-media]');
    if (!wrap) return;
    var section = wrap.closest('[data-parallax-section]') || wrap.parentElement;
    if (reduceMotion.matches || !window.matchMedia('(min-width: 1024px)').matches) return;

    var current = 0, target = 0, raf = null;

    function measure() {
      var rect = section.getBoundingClientRect();
      var progress = Math.min(1, Math.max(0, -rect.top / rect.height));
      target = progress * 8; /* yPercent: 8 */
    }
    function tick() {
      current += (target - current) * 0.12;
      if (Math.abs(target - current) < 0.01) current = target;
      wrap.style.transform = 'translate3d(0,' + current + '%,0)';
      raf = (current !== target) ? requestAnimationFrame(tick) : null;
    }
    window.addEventListener('scroll', function () {
      measure();
      if (raf === null) raf = requestAnimationFrame(tick);
    }, { passive: true });
    measure();
  }

  /* ---- Scroll-scrubbed product video (desktop /shop hero) -------------
     Plays through the clip over the first 32px of page scroll, exactly
     like the original's manual scrub handler. */
  function initVideoScrub() {
    var video = document.querySelector('[data-scrub-video]');
    if (!video) return;
    if (!window.matchMedia('(min-width: 1024px)').matches) return;

    var SCRUB_PX = 32;
    var dur = 0;

    function tryGetDur() {
      if (dur > 0) return;
      if (video.seekable && video.seekable.length > 0) {
        var end = video.seekable.end(video.seekable.length - 1);
        if (end > 0 && isFinite(end)) { dur = end; return; }
      }
      if (isFinite(video.duration) && video.duration > 0) dur = video.duration;
    }
    function apply() {
      if (!dur) return;
      video.currentTime = dur * Math.min(1, window.scrollY / SCRUB_PX);
    }
    function scrub() { tryGetDur(); apply(); }

    video.pause();
    video.addEventListener('play', function () { video.pause(); });
    ['loadeddata', 'canplay', 'canplaythrough', 'progress'].forEach(function (ev) {
      video.addEventListener(ev, scrub);
    });
    window.addEventListener('scroll', scrub, { passive: true });
    tryGetDur();
    apply();
  }

  function initAll(root) {
    initReveals(root);
    initLoadReveals(root);
    initCountUps(root);
  }

  document.addEventListener('DOMContentLoaded', function () {
    initAll(document);
    initParallax();
    initVideoScrub();
  });

  /* Theme editor: re-run when a section is re-rendered. */
  document.addEventListener('shopify:section:load', function (e) {
    initAll(e.target);
  });

  window.DDMotion = { initReveals: initReveals, initAll: initAll };
})();
